import React, { Component } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Dimensions from 'react-dimensions';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import withWidth from '@material-ui/core/withWidth';
import Typography from '@material-ui/core/Typography/Typography';

import MenuBar from './bar/MenuBar';
import ErrorBar from './bar/ErrorBar';
import Login from './auth/Login';
import { WithWidthHome as Home } from './Home/Home';
import CreateAndEditResource from './crud/CreateAndEditResource';
import CreateAndEditTier from './tier/CreateAndEditTier';
import { CreateEditModifier } from './crud/CreateEditModifier/CreateEditModifier';
import ListResources from './crud/ListResources';
import * as authActions from '../redux/auth/AuthActions';
import * as resourcesActions from '../redux/resource/ResourcesActions';
import * as itemsSelectActions from '../redux/itemsSelect/ItemsSelectActions';
import Unauthorized from './auth/Unauthorized';
import { isDeviceView } from '../utils/Functions';
import { appPageStyles, generalDialogStyles } from '../css/style';
import GeneralDialog from './share/GeneralDialog';
import * as CompanyFeatures from '../services/CompanyFeatures';
import { translation } from '../utils/Functions';
import * as PromiseAllSettled from '../utils/PromiseAllSettled';
import { Userpilot } from 'userpilot';
import CreateAndEditCoupon from './coupon/CreateAndEditCoupon';
import CreateAndEditLocation from './location/CreateAndEditLocation';
import * as tiersActions from '../redux/tiers/TiersActions';
import * as couponsActions from '../redux/coupons/CouponsActions';
import * as taxesActions from '../redux/taxes/TaxesActions';
import CreateAndEditTax from './tax/CreateAndEditTax';

PromiseAllSettled.polyfillPromiseAllSettled();

const styles = appPageStyles();
const dialogStyles = generalDialogStyles();

const CreateEditPages = Object.freeze({
  customizations: CreateEditModifier,
});

const createAndEditRenderFunc = createEditProps => (routeProps) => {
  const { resource } = routeProps.match.params;
  const { admin } = createEditProps;

  const isSharedOptionsEnabled = CompanyFeatures.isSharedOptionsEnabled(admin);
  let PageComponent = isSharedOptionsEnabled
    ? CreateEditPages[resource] || CreateAndEditResource
    : CreateAndEditResource;

  //Temporary flag for development
  const isNewLocationPage = true;

  if (resource === 'tiers') {
    PageComponent = CreateAndEditTier;
  } else if (resource === 'coupons') {
    PageComponent = CreateAndEditCoupon;
  } else if (isNewLocationPage && resource === 'locations') {
    PageComponent = CreateAndEditLocation;
  } else if (resource === 'taxes') {
    PageComponent = CreateAndEditTax;
  }

  return (
    <PageComponent {...routeProps} {...createEditProps} />
  );
};

/* eslint-disable react/prefer-stateless-function */
class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showEnterpriseTierDialog: false,
    };
  }

  componentDidMount = async () => {
    const { admin, actions } = this.props;
    // If someone has clicked Remember Me, 
    // then new windows should prioritize reloading the signed in user
    if (!admin && localStorage.admin) {
      await actions.setAdmin(JSON.parse(localStorage.getItem('admin')));
      return;
    }

    // Places admin into the Redux Store 
    // so that deeply nested store-connected components can access it without explicit prop passing
    if (!admin && sessionStorage.admin) {
      await actions.setAdmin(JSON.parse(sessionStorage.getItem('admin')));
    }
  }

  componentDidUpdate = async (prevProps) => {
    await this.getCompanyFeaturesIfUnavailable();
  }

  getCompanyFeaturesIfUnavailable = async () => {
    const { admin, actions } = this.props;
    if (admin && !admin.features) {
      const { token, administeredCompanies = [], company } = admin;
      const featuresValues = await actions.getCompanyFeatures(
        token,
        company,
        administeredCompanies,
      );
      const adminClone = cloneDeep(admin);
      adminClone.features = CompanyFeatures.getFeaturesByCompanyMap(featuresValues);
      await actions.setAdmin(adminClone);
      sessionStorage.setItem('admin', JSON.stringify(adminClone));
    }

    if (admin) {
      Userpilot.reload();
    }
  }

  toggleEnterpriseDialog = targetState => () => this.setState({
    showEnterpriseTierDialog: targetState,
  });

  enterpriseTierDialogMessage = () => (
    <Typography style={dialogStyles.generalMessage}>
      {translation('dialogs.enterpriseTier.messageStart')}
      {<a href="mailto:sales@craverapp.com">sales@craverapp.com</a>}
      {translation('dialogs.enterpriseTier.end')}
    </Typography>
  )

  render() {
    const {
      actions,
      loading,
      resources,
      errors,
      admin,
      viewMoreLoading,
      requestsLeftToLoad,
      width,
      selectedItems,
    } = this.props;
    // TODO: Add a filter reducer to get filter information from state
    const millisecsInOneDay = 86400000;
    const at30daysAgo = Date.now() - millisecsInOneDay * 30;
    const filter = {
      startDate: new Date(at30daysAgo),
      endDate: new Date(),
    };
    const localAdmin = admin || (sessionStorage.getItem('admin') !== null
      ? JSON.parse(sessionStorage.getItem('admin'))
      : null);

    const commonCreateEditProps = {
      loading,
      resources,
      actions,
      apiToken: localAdmin && localAdmin.token,
      admin: localAdmin,
    };
    const commonCreateProps = {
      ...commonCreateEditProps,
      type: 'create',
    };

    const commonEditProps = {
      ...commonCreateEditProps,
      type: 'edit',
      requestsLeftToLoad,
    };

    return (
      <BrowserRouter>
        {
          loading === false && localAdmin === null
            ? <Login actions={actions} errors={errors.errors} />
            : (
              <div className="App" style={{ ...styles.container, paddingTop: isDeviceView('md', width) ? '15px' : 0 }}>
                <MenuBar
                  actions={actions}
                  admin={localAdmin}
                  onClickLocked={this.toggleEnterpriseDialog(true)}
                />
                <div style={styles.tabs}>
                  <GeneralDialog
                    open={this.state.showEnterpriseTierDialog}
                    onClose={this.toggleEnterpriseDialog(false)}
                    message={this.enterpriseTierDialogMessage()}
                    title={translation('dialogs.enterpriseTier.title')}
                  />
                  <Switch>
                    <Route path="/unauthorized" exact name="adminUnauthorized" render={props => <Unauthorized admin={localAdmin} {...props} />} />
                    <Route path="/:resource/:subResource/create" exact name="createSubResource" render={() => <CreateAndEditResource type="create" loading={loading} resources={resources} actions={actions} apiToken={localAdmin.token} admin={localAdmin} />} />
                    <Route path="/" exact name="home" render={() => <Home resources={resources} loading={loading} actions={actions} apiToken={localAdmin.token} filter={filter} />} />
                    <Route path="/:resource/" exact name="resources" render={() => <ListResources loading={loading} viewMoreLoading={viewMoreLoading} resources={resources} actions={actions} apiToken={localAdmin.token} filter={filter} admin={localAdmin} selectedItems={selectedItems} />} />
                    <Route path="/:resource/create" exact name="createResource" render={createAndEditRenderFunc(commonCreateProps)} />
                    <Route path="/:resource/:resourceId/edit" exact name="editResource" render={createAndEditRenderFunc(commonEditProps)} />
                    <Route path="/:resource/:resourceId/:subResource" exact name="subResources" render={() => <ListResources loading={loading} resources={resources} actions={actions} apiToken={localAdmin.token} filter={filter} admin={localAdmin} />} />
                    <Route path="/:resource/:resourceId/:subResource/create" exact name="createSubResource" render={() => <CreateAndEditResource type="create" loading={loading} resources={resources} actions={actions} apiToken={localAdmin.token} admin={localAdmin} />} />
                    <Route path="/:resource/:resourceId/:subResource/:subResourceId/edit" exact name="editSubResource" render={() => <CreateAndEditResource type="edit" loading={loading} resources={resources} actions={actions} apiToken={localAdmin.token} admin={localAdmin} />} />
                  </Switch>
                </div>
                <ErrorBar errors={errors.errors} actions={actions} />
              </div>
            )
        }
      </BrowserRouter>
    );
  }
}

App.propTypes = {
  loading: PropTypes.bool.isRequired,
  resources: PropTypes.objectOf(PropTypes.any),
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  admin: PropTypes.objectOf(PropTypes.any),
  errors: PropTypes.objectOf(PropTypes.any),
  viewMoreLoading: PropTypes.number.isRequired,
  requestsLeftToLoad: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  width: PropTypes.number.isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
};

App.defaultProps = {
  resources: {},
  admin: null,
  errors: null,
};

export default connect(
  state => ({
    loading: state.get('loading').toJS().loading,
    viewMoreLoading: state.get('loading').toJS().viewMoreLoading,
    requestsLeftToLoad: state.get('loading').toJS().requestsLeftToLoad,
    resources: state.get('resources').toJS(),
    errors: state.get('errors').toJS(),
    admin: state.get('admin').toJS().admin,
    selectedItems: state.get('selectedItems').toJS().selectedItems,
  }),
  dispatch => ({
    actions: bindActionCreators({
      ...authActions,
      ...resourcesActions,
      ...tiersActions,
      ...couponsActions,
      ...itemsSelectActions,
      ...taxesActions,
    }, dispatch),
  }),
)(Dimensions({ elementResize: true })(withWidth()(App)));
